<template>
  <v-breadcrumbs
    :class="{
      'menu-drilldown-breadcrumbs': true,
      'menu-drilldown-breadcrumbs--shadow': shadow,
    }"
    :items="drill"
    id="v-breadcrumbs"
    :mobile-break-point="100500"
  >
    <template #divider>
      <ui-icon
        class="menu-drilldown-breadcrumbs__icon"
        icon="fas fa-angle-right"
      ></ui-icon>
    </template>

    <template #item="{ item: drillItem }">
      <ui-chip
        :ref="(el) => registerChipRef(drillItem.index, el)"
        class="ml-4 ml-first-0 menu-drilldown-breadcrumbs__item"
        :prepend-icon="getIcon(drillItem)"
        :has-close="drillItem.index"
        @click="handleItemClick(drillItem)"
        @close="emit('close', drillItem.index)"
      >
        <div class="menu-drilldown-breadcrumbs__chip-grouping ml-4 ml-first-0 text-capitalize">{{ getGrouping(drillItem.filterTables) }}:</div>

        <div class="menu-drilldown-breadcrumbs__text ml-4 ml-first-0">{{ getText(drillItem) }}</div>

        <div v-if="drillItem.children && drillItem.children.length">
          <ui-menu
            v-model="menuOpened[drillItem.key]"
            :has-triangle="true"
            location="bottom start"
            :offset="[drillItem.index ? 25 : 8, 16]"
            :items="getMenuList(drillItem)"
            :menu-props="{
              menuWidth: 188
            }"
          >
            <template #activator>
              <ui-chevron
                :open="menuOpened[drillItem.key]"
                class="ml-4"
                @click.stop="handleMenu(drillItem.key)"
              ></ui-chevron>
            </template>

            <template #item="{ item }">
              <menu-drilldown-menu-list-item
                :title="item.title"
                :has-close="item.key !== 'closeToTheRight'"
                @close="emit('slice-exclude', item.meta)"
              ></menu-drilldown-menu-list-item>
            </template>
          </ui-menu>
        </div>
      </ui-chip>
    </template>
  </v-breadcrumbs>
</template>

<script setup lang="ts">
import { type ComponentPublicInstance, reactive, watch } from 'vue';
import { get } from 'lodash';
import { useMainMenuComplexTabsMeta } from '@/composables';
import { DrillFilterOptionsEnum } from '@/services/models/Enums';
import helpers from '@/services/helpers';
import MenuDrilldownMenuListItem from '@/views/reports/views/menu-drilldown/menu-drilldown-menu-list-item.vue';

export interface MenuDrilldownBreadcrumbsWidths {
  widths: number[];
  dividerWidth: number;
}

const props = defineProps<{
  drill: any[];
  shadow?: boolean;
}>();

const emit = defineEmits<{
  click: [index: number];
  close: [index: number];
  resize: [data: MenuDrilldownBreadcrumbsWidths];
  'slice-exclude': [data: Bemob.ReportQueryDrill];
}>();

const { tabsMeta } = useMainMenuComplexTabsMeta();

const menuOpened = reactive<Record<string, boolean>>({});
const chipRefs: Record<string, ComponentPublicInstance | Element | null> = {};

const registerChipRef = (index: number, el: Element | ComponentPublicInstance | null) => {
  chipRefs[`chip_${index}`] = el;
};

const handleItemClick = (item: { children?: unknown[]; key: string; index: number }) => {
  if (item.children && item.children.length) {
    handleMenu(item.key);
  } else {
    emit('click', item.index);
  }
};

const handleMenu = (key: string) => {
  menuOpened[key] = !menuOpened[key];
};

const getMenuList = (item: { children: any[]; index: number; last?: boolean }): Bemob.ListItem[] => {
  const children = item.children;
  const closeToTheRight: Bemob.ListItem = {
    key: 'closeToTheRight',
    title: 'Close to the right',
    click: () => emit('click', item.index),
  };

  let result = children
    .map<Bemob.ListItem>((child, index) => ({
      key: index,
      title: child.text,
      meta: child,
    }));

  if (!item.last) {
    result.push(closeToTheRight);
  }

  return result;
};

const getIcon = (item: Bemob.ReportQueryDrill) =>
  item.filterOptions === DrillFilterOptionsEnum.not_equals
    ? 'far fa-page-break'
    : 'far fa-random';

const getGrouping = (type: string) => {
  const key = `[${type}].title`;
  const title: string = get(tabsMeta.value, key, null);

  return title || '';
};

const getText = (item: { text: string; children?: unknown[] }) => {
  const result = [item.text];

  if (item.children && item.children.length > 1) {
    result.push(`[+${item.children.length - 1}]`);
  }

  return result.join(' ');
};

const recalcChipsWidth = (value: any[] = props.drill) => {
  const widths = value.map((drillItem) => {
    const node = chipRefs[`chip_${drillItem.index}`];
    const el = node && '$el' in node
      ? (node as ComponentPublicInstance).$el as HTMLElement
      : node as HTMLElement;
    const rect = el?.getBoundingClientRect?.();

    return rect?.width;
  });

  emit('resize', {
    widths,
    dividerWidth: 16,
  });
};

watch(() => props.drill, (value) => {
  if (props.shadow) {
    helpers.afterBrowserRedraw(() => {
      recalcChipsWidth(value);
    });
  }
}, { immediate: true, deep: true });
</script>

<style lang="scss">
.menu-drilldown-breadcrumbs {
  flex-wrap: nowrap;
  padding: 0px 0px;

  &__item {
    max-width: 100%;
    cursor: pointer;
    margin-top: 0;
    margin-bottom: 0;

    &:hover {
      background-color: var(--color-green20);
    }
  }

  &__chip-grouping {
    font-weight: bold;
  }

  &--shadow {
    opacity: 0;
    position: absolute;
    top: -9999px;
    left: -9999px;
  }

  .v-breadcrumbs__divider:nth-child(2n){
    padding: 0 4px;
  }

  & &__icon {
    font-size: 12px;
    line-height: 1.33;
    width: 8px;
  }

  &__text {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
